import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, Phone, BadgeCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  formatFirebasePhoneError,
  isFirebasePhoneConfigured,
  resetFirebaseRecaptcha,
  sendFirebasePhoneCode,
} from "../lib/firebasePhone";

function cleanUsPhoneInput(value) {
  let digits = value.replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("1")) digits = digits.slice(1);
  return digits.slice(0, 10);
}

function formatUsPhone(digits) {
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function VerifyPhoneDialog({ open, onClose, onVerified }) {
  const { refreshUser, user } = useAuth();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [err, setErr] = useState("");
  const [sentMessage, setSentMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const otpRequestId = useRef(0);

  const close = useCallback(() => {
    resetFirebaseRecaptcha();
    setStep(1);
    setPhone("");
    setCode("");
    setConfirmationResult(null);
    setErr("");
    setSentMessage("");
    otpRequestId.current += 1;
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open || step !== 2 || !user?.phone_verified) return;
    Promise.resolve(onVerified?.()).catch(() => {});
    toast.success("Phone verified");
    close();
  }, [close, onVerified, open, step, user?.phone_verified]);

  if (!open) return null;

  const normalizedPhone = phone.length === 10 ? `+1${phone}` : "";
  const displayCustomerPhone = normalizedPhone ? `+1 ${formatUsPhone(phone)}` : "+1";

  const requestOtp = async (e) => {
    e.preventDefault();
    setErr("");
    if (phone.length !== 10) {
      setErr("Enter a 10-digit US phone number.");
      return;
    }
    const requestId = otpRequestId.current + 1;
    otpRequestId.current = requestId;
    setLoading(true);
    setSentMessage("");
    try {
      if (!isFirebasePhoneConfigured()) {
        setErr("Phone verification is not configured. Ask support to enable SMS verification.");
        return;
      }
      resetFirebaseRecaptcha();
      const confirmation = await sendFirebasePhoneCode(normalizedPhone);
      if (requestId !== otpRequestId.current) return;
      setConfirmationResult(confirmation);
      setStep(2);
      setSentMessage(`SMS sent to ${displayCustomerPhone}. If it does not arrive, wait 30 seconds and tap Resend.`);
      toast.success("Verification code sent");
    } catch (e) {
      if (requestId !== otpRequestId.current) return;
      resetFirebaseRecaptcha();
      setErr(formatFirebasePhoneError(e) || formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const changeNumber = () => {
    resetFirebaseRecaptcha();
    setConfirmationResult(null);
    setCode("");
    setErr("");
    setSentMessage("");
    setStep(1);
  };

  const confirmOtp = async (e) => {
    e.preventDefault();
    setErr("");
    if (!code) {
      setErr("Enter the 6-digit SMS code.");
      return;
    }
    if (!confirmationResult) {
      setErr("Send a new verification code first.");
      return;
    }
    setLoading(true);
    try {
      const credential = await confirmationResult.confirm(code);
      const idToken = await credential.user.getIdToken();
      const response = await api.post("/auth/phone/firebase", { phone: normalizedPhone, id_token: idToken });
      await refreshUser();
      await onVerified?.();
      if (response?.data?.free_play_credited) {
        toast.success("$5 Freeplay added");
      } else if (response?.data?.free_play_blocked_reason === "profile_details_required") {
        toast.info("Phone verified. Add full legal name, date of birth, and state to claim $5 Freeplay.");
      } else {
        toast.success("Phone verified");
      }
      close();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/25 backdrop-blur-[2px] grid place-items-center p-4" onClick={close}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="verify-phone-dialog"
        className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-7 text-slate-950 shadow-2xl"
      >
        <div id="firebase-recaptcha-container" />
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#EAF4FB] border border-[#BFDDF3] grid place-items-center">
              <Phone className="w-4 h-4 text-[#2F6FA5]" />
            </div>
            <div>
              <h3 className="font-display text-xl font-bold leading-tight text-slate-950">Verify phone number</h3>
              <p className="text-xs text-slate-600">Verify once to claim $5 Freeplay for game loads</p>
            </div>
          </div>
          <button onClick={close} data-testid="verify-close" className="p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {step === 1 && (
          <form onSubmit={requestOtp} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-600 mb-2">Phone number</label>
              <div className="flex rounded-lg border border-slate-300 bg-white focus-within:border-[#2F6FA5] focus-within:ring-2 focus-within:ring-[#2F6FA5]/15">
                <div className="grid place-items-center border-r border-slate-200 bg-slate-50 px-4 py-3 font-bold text-[#2F6FA5]">
                  +1
                </div>
                <input
                  type="tel"
                  required
                  value={formatUsPhone(phone)}
                  onChange={(e) => setPhone(cleanUsPhoneInput(e.target.value))}
                  placeholder="(555) 123-4567"
                  data-testid="verify-phone-input"
                  className="min-w-0 flex-1 bg-transparent px-4 py-3 text-slate-950 placeholder:text-slate-400 outline-none"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                US numbers only. Verified players get $5 Freeplay for game loads.
              </p>
            </div>

            {err && (
              <div data-testid="verify-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="verify-request-otp"
              className="w-full py-3 bg-[#2F6FA5] text-white font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#4E91C8] transition-all disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send verification code"}
            </button>

            {loading && (
              <p className="text-center text-xs text-slate-500">
                Keep this page open while the SMS code is sending.
              </p>
            )}
          </form>
        )}

        {step === 2 && (
          <form onSubmit={confirmOtp} className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-2xl bg-[#EAF4FB] border border-[#BFDDF3] grid place-items-center">
                <MessageCircle className="w-5 h-5 text-[#2F6FA5]" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold leading-tight text-slate-950">Enter SMS code</h3>
                <p className="text-sm text-slate-600">Code sent to {displayCustomerPhone}</p>
              </div>
            </div>

            {sentMessage && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                {sentMessage}
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-600 mb-2">6-digit code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                data-testid="verify-code-input"
                className="w-full px-4 py-3 rounded-lg bg-white border border-slate-300 text-slate-950 placeholder:text-slate-300 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/15 text-center text-2xl tracking-[0.5em] font-mono"
              />
            </div>

            {err && (
              <div data-testid="verify-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="verify-confirm-otp"
              className="w-full py-3 bg-[#2F6FA5] text-white font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#4E91C8] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <BadgeCheck className="w-4 h-4" /> {loading ? "Verifying..." : "Verify phone"}
            </button>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={requestOtp}
                disabled={loading}
                className="rounded-full border border-slate-300 px-3 py-2 text-sm font-bold text-[#2F6FA5] hover:bg-slate-50 disabled:opacity-50"
              >
                Resend code
              </button>
              <button
                type="button"
                onClick={changeNumber}
                disabled={loading}
                className="rounded-full border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Change number
              </button>
            </div>

          </form>
        )}

        <p className="mt-6 text-[10px] text-slate-500 text-center leading-relaxed">
          Your phone verifies only after Firebase confirms the SMS code for the same phone number.
        </p>
      </div>
    </div>
  );
}
